import { createFileRoute, Link } from "@tanstack/react-router";
import { gatedRobotsMeta } from "@/lib/seo-gating";
import { ArrowLeft, Users } from "lucide-react";
import { BookmarkButton } from "@/components/learn/BookmarkButton";
import { LearnQA } from "@/components/learn/LearnQA";
import { LearnCarousel, LEARN_THEMES } from "@/components/learn/LearnCarousel";
import { IFS_INTRO_CARDS } from "@/data/learn/ifs-intro-cards";


export const Route = createFileRoute("/learn/what-is-ifs")({
  head: () => ({
    meta: [
      { title: "What is IFS? — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A gentle ten-card introduction to Internal Family Systems: parts, protectors, exiles and the calm Self underneath. Trauma-informed education from Recovery Trauma™.",
      },
      { property: "og:title", content: "What is IFS? — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Ten short cards explaining Internal Family Systems in plain English — no jargon, nothing to get right.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/what-is-ifs" },
      { name: "twitter:card", content: "summary_large_image" },
      ...gatedRobotsMeta("/learn/what-is-ifs"),
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/what-is-ifs" }],
  }),
  component: WhatIsIfsPage,
});

const CARDS = IFS_INTRO_CARDS;


function WhatIsIfsPage() {
  const theme = LEARN_THEMES.lavender!;
  return (
    <main className={`min-h-screen ${theme.pageBg} px-4 pb-16 pt-6`}>
      <div className="mx-auto w-full max-w-3xl">
        <Link
          to="/learn"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Learn
        </Link>

        <header className="mt-4 flex items-start justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-violet-700 dark:text-violet-300">
              <Users className="h-4 w-4" aria-hidden />
              Parts work
            </p>
            <h1 className="mt-2 font-serif text-3xl text-foreground">What is IFS?</h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Ten short cards introducing Internal Family Systems in plain English. Swipe through at
              your own pace — you can stop anywhere.
            </p>
          </div>
          <BookmarkButton
            id="learn:what-is-ifs"
            title="What is IFS?"
            href="/learn/what-is-ifs"
          />
        </header>

        <div className="mt-6">
          <LearnCarousel
            cards={CARDS}
            theme={theme}
            storageKey="what-is-ifs"
            showContents
            showCardNav
          />
        </div>

        <LearnQA
          items={[
            {
              q: "What is Internal Family Systems (IFS) therapy?",
              a: "IFS is a therapy model developed by Richard Schwartz that treats the mind as made up of parts — protectors, firefighters and exiles — with a calm Self underneath that is never damaged. Rather than removing parts, the work is about getting to know them and easing the burdens they carry.",
            },
            {
              q: "What are parts and exiles in IFS?",
              a: "Parts are the familiar inner voices and reactions: the critic, the planner, the one who shuts down. Exiles are the younger, hurt parts that carry pain, which protectors work hard to keep out of awareness. Protectors are not enemies; they took their jobs for a reason.",
            },
            {
              q: "What is Self in IFS?",
              a: "Self is the steady quality underneath the parts, often described through qualities such as calm, curiosity, compassion and clarity. IFS holds that it cannot be destroyed by what happened to you — only covered over.",
            },
            {
              q: "Is IFS good for complex trauma?",
              a: "Many people with complex trauma find the parts language fits their experience, and therapists value the emphasis on not overwhelming protectors. Its trauma-specific trial base is smaller than for EMDR and trauma-focused CBT, so it is best described as promising and widely used rather than first-line in clinical guidance.",
            },
            {
              q: "Can you do IFS on your own?",
              a: "You can safely get to know a protector, notice its job and thank it. Unburdening exiled parts is deeper work that generally needs a trained therapist, particularly where there is dissociation or ongoing risk.",
            },
          ]}
          links={[
            { label: "Compare the main trauma therapies", to: "/learn/therapies" },
            { label: "What somatic therapy is and what a session involves", to: "/blog/what-is-somatic-therapy" },
            { label: "Emotional flashbacks and CPTSD", to: "/blog/emotional-flashbacks-cptsd" },
            { label: "How to regulate your nervous system after trauma", to: "/blog/how-to-regulate-your-nervous-system-after-trauma" },
          ]}
        />

        <p className="mt-8 text-center text-sm text-muted-foreground">
          Want to go deeper?{" "}
          <Link to="/learn/$topic" params={{ topic: "ifs" }} className="font-medium text-foreground underline underline-offset-4">
            Open the full IFS area
          </Link>
        </p>
      </div>
    </main>
  );
}
